import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { logger } from '../../utils/logger';

interface TierProgress {
  can_upgrade: boolean;
  current_tier: string;
  next_tier?: string;
  total_passes: number;
  threshold?: number;
  remaining?: number;
  progress_percentage?: number;
  message?: string;
  next_tier_config?: {
    display_name: string;
    price: number;
    monthly_passes: number;
    users: number;
  };
}

const ProgressCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  overflow: 'visible',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
    borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
  }
}));

const GradientProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 8,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  '& .MuiLinearProgress-bar': {
    borderRadius: 8,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
    boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.5)}`,
  }
}));

const TierUpgradeProgress: React.FC = () => {
  const [progress, setProgress] = useState<TierProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUpgradeProgress();
  }, []);

  const fetchUpgradeProgress = async () => {
    try {
      setLoading(true);

      const response = await api.get('/companies/me/tier-progress');
      const data = response.data;
      setProgress(data);
    } catch (err) {
      logger.error('Error fetching upgrade progress:', err);
      setError(err instanceof Error ? err.message : 'Failed to load upgrade progress');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Učitavanje...
            </Typography>
            <LinearProgress sx={{ flexGrow: 1, height: 4, borderRadius: 2 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !progress) {
    return null; // Don't show anything if error or no progress available
  }

  // Don't show if tier cannot be upgraded (FREE or ENTERPRISE)
  if (!progress.can_upgrade) {
    return null;
  }

  const progressPercentage = progress.progress_percentage || 0;
  const remaining = progress.remaining || 0;
  const nextTierConfig = progress.next_tier_config;

  return (
    <ProgressCard>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingUpIcon color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>
              Napredak ka Upgrade-u
            </Typography>
            <Tooltip title="Automatski prelazak na viši tier kada dostignete threshold">
              <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>

          {progressPercentage >= 100 ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Upgrade uskoro!"
              color="success"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          ) : (
            <Chip
              label={`${progressPercentage.toFixed(0)}%`}
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="caption" color="text.secondary">
              <strong>{progress.total_passes}</strong> / {progress.threshold} passes
            </Typography>
            <Typography variant="caption" fontWeight={600} color="primary.main">
              Još <strong>{remaining}</strong> passes
            </Typography>
          </Box>

          <GradientProgress
            variant="determinate"
            value={Math.min(progressPercentage, 100)}
          />
        </Box>

        {nextTierConfig && (
          <Alert
            severity={progressPercentage >= 80 ? "success" : "info"}
            icon={progressPercentage >= 80 ? <CheckCircleIcon /> : <TrendingUpIcon />}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: '1.25rem'
              }
            }}
          >
            <Typography variant="body2" fontWeight={500}>
              {progressPercentage >= 100
                ? `✨ Prelazite na ${nextTierConfig.display_name} automatski!`
                : `Na ${progress.threshold} passes → ${nextTierConfig.display_name}`
              }
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              €{nextTierConfig.price}/mesec • {nextTierConfig.monthly_passes} passes • {nextTierConfig.users} korisnika
            </Typography>
          </Alert>
        )}
      </CardContent>
    </ProgressCard>
  );
};

export default TierUpgradeProgress;
